import { FC, useState } from 'react';
import {
  Button,
  Icon,
  PageSection,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';

import { useGenericModal } from '../../modals';
import type {
  LanguageCatalogEntry,
  SpeechRecognition,
} from '../language-catalog';
import {
  DEFAULT_AUDIO_MODEL,
  languageKeyOf,
  type LanguageEntry,
} from '../languages-config';
import type {
  LanguageEntryError,
  LanguagesValidation,
} from '../validate-languages';
import { AddLanguageModal } from './add-language-modal';
import {
  ERROR_RED,
  LanguageEntryCard,
  type AudioModelOption,
} from './language-entry-card';

/**
 * Errors about a choice the user already made — shown as soon as it is made,
 * since PRD §4 wants problems surfaced at the point of configuration. The
 * "not chosen yet" ones (no voice, no model) wait for a save attempt, or every
 * freshly added language would open in red.
 */
const IMMEDIATE_ERRORS: LanguageEntryError[] = [
  'google-only',
  'english-only-model',
  'unknown-locale',
  'duplicate-language',
];

/** Voice settings a newly added language starts from (as a new channel does). */
const NEW_ENTRY_DEFAULTS = {
  elevenlabsAudioModel: DEFAULT_AUDIO_MODEL,
  elevenlabsVoiceId: '',
  elevenlabsSpeed: 0.8,
  elevenlabsStability: 0.8,
  elevenlabsSimilarity: 0.6,
};

export interface LanguagesBlockProps {
  languages: LanguageEntry[];
  startingLanguage: string;
  onLanguagesChange: (
    languages: LanguageEntry[],
    startingLanguage: string
  ) => void;
  catalog: LanguageCatalogEntry[];
  catalogLoading: boolean;
  catalogError: unknown;
  onRetryCatalog: () => void;
  speechRecognition: SpeechRecognition;
  validation: LanguagesValidation;
  /** A save was attempted: show every problem, not only the immediate ones. */
  showErrors: boolean;
  audioModels: AudioModelOption[];
  onChooseVoice: (index: number) => void;
}

/**
 * The Languages block of a Voice AI channel (PSTUDIO-2950): one voice per
 * language, one of them the language every call starts in. Replaces the single
 * Voice section. Speech recognition stays a channel-wide section above it.
 */
export const LanguagesBlock: FC<LanguagesBlockProps> = ({
  languages,
  startingLanguage,
  onLanguagesChange,
  catalog,
  catalogLoading,
  catalogError,
  onRetryCatalog,
  speechRecognition,
  validation,
  showErrors,
  audioModels,
  onChooseVoice,
}) => {
  const { modalRef, openModal, closeModal } = useGenericModal();

  // Expanded entries, by key. Until the user opens or closes one, a
  // single-language channel shows its only entry expanded, so it reads like
  // the old Voice section; derived rather than seeded so a channel that loads
  // after the first render gets the same treatment.
  const [touchedOpenKeys, setOpenKeys] = useState<string[] | null>(null);
  const openKeys =
    touchedOpenKeys ?? (languages.length === 1 ? [languages[0].key] : []);

  const setOpen = (key: string, open: boolean) =>
    setOpenKeys(
      open
        ? [...new Set([...openKeys, key])]
        : openKeys.filter((k) => k !== key)
    );

  const addLocales = (locales: string[]) => {
    const added: LanguageEntry[] = locales.map((locale) => ({
      key: languageKeyOf(locale),
      locale,
      ...NEW_ENTRY_DEFAULTS,
    }));
    const next = [...languages, ...added];
    onLanguagesChange(
      next,
      next.some((l) => l.key === startingLanguage)
        ? startingLanguage
        : next[0].key
    );
    setOpenKeys([...new Set([...openKeys, ...added.map((a) => a.key)])]);
  };

  const updateEntry = (index: number, patch: Partial<LanguageEntry>) => {
    const previousKey = languages[index].key;
    const next = languages.map((entry, i) =>
      i === index ? { ...entry, ...patch } : entry
    );
    const newKey = next[index].key;
    // The starting language follows its entry when the entry changes language.
    onLanguagesChange(
      next,
      startingLanguage === previousKey ? newKey : startingLanguage
    );
    if (newKey !== previousKey) {
      setOpenKeys(openKeys.map((k) => (k === previousKey ? newKey : k)));
    }
  };

  const removeEntry = (index: number) => {
    const removed = languages[index];
    const next = languages.filter((_, i) => i !== index);
    onLanguagesChange(
      next,
      removed.key === startingLanguage ? (next[0]?.key ?? '') : startingLanguage
    );
    // The focused Remove button is gone with its entry; hand focus to the
    // entry that took its place (or the one before), else to Add Language
    // (WCAG 2.4.3).
    const neighbour = next[Math.min(index, next.length - 1)];
    requestAnimationFrame(() => {
      const target = neighbour
        ? document.querySelector<HTMLElement>(
            `button[test-id="ps-voice-language-${neighbour.key}-toggle"]`
          )
        : document.querySelector<HTMLElement>(
            'ps-button[test-id="ps-voice-add-language-button"]'
          );
      target?.focus();
    });
  };

  const keys = languages.map((l) => l.key);
  const visibleErrors = (index: number) =>
    (validation.entryErrors[index] || []).filter(
      (error) => showErrors || IMMEDIATE_ERRORS.includes(error)
    );
  const summaryVisible =
    !!validation.summary &&
    (showErrors || languages.some((_, i) => visibleErrors(i).length > 0));

  return (
    <PageSection size="small">
      <Stack justifyContent="space-between" alignItems="center" nowrap>
        <div>
          <TextTitle size="xsmall" test-id="ps-voice-languages-title">
            Languages
          </TextTitle>
          <TextBody variant="secondary">
            Configure each language. Voice AI switches automatically.
          </TextBody>
        </div>
        <Button
          size="large"
          onClick={openModal}
          disabled={catalogLoading || !!catalogError}
          test-id="ps-voice-add-language-button"
        >
          <Icon slot="prefix" name="plus" size="small" />
          Add Language
        </Button>
      </Stack>
      <div style={{ height: 24 }} />

      {catalogError ? (
        <>
          <Stack
            alignItems="center"
            justifyContent="space-between"
            gutter="small"
            test-id="ps-voice-languages-catalog-error"
            role="alert"
          >
            <TextBody size="small" variant="secondary">
              Couldn&apos;t load the list of supported languages. Saved
              languages are kept, but none can be added or checked until it
              loads.
            </TextBody>
            <Button size="small" variant="secondary" onClick={onRetryCatalog}>
              Retry
            </Button>
          </Stack>
          <Spacer size="medium" />
        </>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {languages.map((entry, index) => (
          <LanguageEntryCard
            key={`${entry.key}-${index}`}
            entry={entry}
            isStarting={entry.key === startingLanguage}
            open={openKeys.includes(entry.key)}
            onOpenChange={(open) => setOpen(entry.key, open)}
            catalog={catalog}
            speechRecognition={speechRecognition}
            otherLanguages={keys.filter((_, i) => i !== index)}
            errors={visibleErrors(index)}
            audioModels={audioModels}
            canRemove={languages.length > 1}
            onChange={(patch) => updateEntry(index, patch)}
            onMakeStarting={() => onLanguagesChange(languages, entry.key)}
            onRemove={() => removeEntry(index)}
            onChooseVoice={() => onChooseVoice(index)}
          />
        ))}
      </div>

      <div role="status" aria-live="polite">
        {summaryVisible && (
          <>
            <Spacer size="medium" />
            <TextBody
              size="small"
              test-id="ps-voice-languages-summary"
              style={{ color: ERROR_RED }}
            >
              {validation.summary}
            </TextBody>
          </>
        )}
      </div>

      <AddLanguageModal
        modalRef={modalRef}
        onClose={closeModal}
        catalog={catalog}
        speechRecognition={speechRecognition}
        takenLanguages={keys}
        onAdd={addLocales}
      />
    </PageSection>
  );
};
